import React, { useEffect, useRef, useState } from 'react';
import { MoreHorizontal, Play } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { t } from '../i18n';
import { getPracticeNoteMetadata } from '../utils/noteTheme';
import PracticeSettings from './PracticeSettings';
import './MapNoteActions.css';

export default function QuickPracticeAction({ source, note, disabled = false, renderTrigger }) {
  const [menu, setMenu] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const container = useRef(null);
  const trigger = useRef(null);
  const starting = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const practiceNote = getPracticeNoteMetadata(note);
  const open = () => { setMenu(false); setError(''); setSettingsOpen(true); };
  useEffect(() => {
    if (!menu) return undefined;
    container.current?.querySelector('[role="menuitem"]')?.focus();
    const outside = event => { if (!container.current?.contains(event.target)) setMenu(false); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [menu]);
  const start = async (settings, { listening = false } = {}) => {
    if (starting.current) return;
    starting.current = true; setBusy(true); setError('');
    try {
      if (listening) {
        const profile = await apiClient.getCurrentUser();
        if (profile?.entitlements?.canUseTts !== true) {
          throw new Error(t('membership.tts.message'));
        }
      }
      const access = await apiClient.startPractice({ timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, mode: 'quick', source });
      const sharedContext = {
        access,
        flashcards: access.cards,
        returnTo: location.pathname,
        returnState: location.state,
        settings: { ...settings, practiceMode: 'quick', quickSource: source },
        note: practiceNote,
      };
      if (listening) {
        navigate('/practice-player', { state: { playerContext: {
          ...sharedContext,
          mode: settings.playerMode,
          practiceMode: 'quick',
          includeAllCards: true,
          source,
        } } });
      } else {
        navigate('/practice', { state: { testContext: {
          type: 'quick',
          practiceMode: 'quick',
          ...sharedContext,
        } } });
      }
    } catch (err) { setError(err.message || t('quickPractice.failed')); }
    finally { starting.current = false; setBusy(false); }
  };
  return <div ref={container} className="map-note-actions quick-practice-actions" onKeyDown={event => {
    if (event.key === 'Escape' && menu) { setMenu(false); trigger.current?.focus(); }
  }}>
    {renderTrigger ? renderTrigger(open, busy) : <div className="map-note-more">
      <button ref={trigger} type="button" className="map-note-action map-note-action-more" aria-label={t('quickPractice.actions')} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)} disabled={disabled || busy}><MoreHorizontal size={21} /></button>
      {menu && <div role="menu" aria-label={t('quickPractice.actions')} className="map-note-menu"><button role="menuitem" onClick={open}><Play size={16} />{t('quickPractice.title')}</button></div>}
    </div>}
    {settingsOpen && <div>{busy && <p role="status">{t('quickPractice.loading')}</p>}
      {!busy && <PracticeSettings isOpen description={t('quickPractice.description')} errorMessage={error} onClose={() => setSettingsOpen(false)} onStart={start} onStartPlayer={settings => start(settings, { listening: true })} />}
    </div>}
    {error && !settingsOpen && <p role="alert">{error}</p>}
  </div>;
}
